import { createRouter as createVueRouter, createWebHistory, createMemoryHistory } from 'vue-router';

import RouteHandler from './RouteHandler.vue';
import { captureLinkSource } from '@/utils/linksource-utils';
import { captureOnlineMessagePreviousUrl } from '@/utils/online-message';
import { getCountryFromCookie } from '@/utils/site-utils';
import { getLangFromUrl } from '@/utils/uri-utils';
import { waitForRouteScrollReady } from '@/utils/route-scroll-utils';

// support languages in the URL prefix
// e.g. /da-DK/path, or /en/path, or /path
// "pageN" suffix routes enable pseudo-static pagination URLs
// e.g. /webinars/page2 renders /webinars with page=2
// "{filter}-pageN" suffix additionally carries the first filter value in the same
// segment, e.g. /webinars/discovery-page2 renders /webinars with filter=discovery, page=2
// (the filter stays out of sitecoreRoute so Layout Service still resolves the base page)
export const routePatterns = [
  '/:lang([a-z]{2}-[A-Z]{2})/:sitecoreRoute(.*)*/:filterPage([^/]+)-page:page(\\d+)',
  '/:lang([a-z]{2})/:sitecoreRoute(.*)*/:filterPage([^/]+)-page:page(\\d+)',
  '/:sitecoreRoute(.*)*/:filterPage([^/]+)-page:page(\\d+)',
  '/:lang([a-z]{2}-[A-Z]{2})/:sitecoreRoute(.*)*/page:page(\\d+)',
  '/:lang([a-z]{2})/:sitecoreRoute(.*)*/page:page(\\d+)',
  '/:sitecoreRoute(.*)*/page:page(\\d+)',
  '/:lang([a-z]{2}-[A-Z]{2})/:sitecoreRoute(.*)*/:category(.*)*/:productCode(.*)-:productName(.*)',
  '/:lang([a-z]{2}-[A-Z]{2})/:sitecoreRoute(.*)*/:productCode(.*)-:productName(.*)',
  '/:lang([a-z]{2}-[A-Z]{2})/:sitecoreRoute(.*)*',
  '/:lang([a-z]{2})/:sitecoreRoute(.*)*',
  '/:sitecoreRoute(.*)*',
];

export function createRouter(isSSR) {
  let router;

  // create an instance of vue-router and configure routes to use the RouteHandler component
  router = createVueRouter({
    history: isSSR ? createMemoryHistory() : createWebHistory(),
    routes: routePatterns.map((routePattern) => {
      return {
        path: routePattern,
        component: RouteHandler,
        props: (route) => ({
          route,
        }),
      };
    }),
    scrollBehavior(to, from, savedPosition) {
      const isInitialNavigation = !from.matched.length;

      // A same-origin page opened with target="_blank" may inherit the opener's history scroll.
      // It is still a fresh page and must not restore the source page's position.
      if (isInitialNavigation) {
        if (to.hash) {
          return {
            el: to.hash,
            behavior: 'smooth',
            top: 0,
          };
        }

        return { top: 0, left: 0, behavior: 'instant' };
      }

      if (savedPosition) {
        const restorePosition = () => {
          if (router.currentRoute.value.fullPath !== to.fullPath) return false;

          return { ...savedPosition, behavior: 'instant' };
        };

        if (to.path === from.path) {
          return restorePosition();
        }

        return waitForRouteScrollReady(to.fullPath, savedPosition.top).then(restorePosition);
      }
      if (to.hash) {
        return {
          el: to.hash,
          behavior: 'smooth',
          top: 0,
        };
      }

      return waitForRouteScrollReady(to.fullPath).then(() => {
        if (router.currentRoute.value.fullPath !== to.fullPath) return false;

        return { top: 0, left: 0, behavior: 'instant' };
      });
    },
  });

  router.beforeEach((_to, from) => {
    if (from.matched.length && from.fullPath) {
      captureOnlineMessagePreviousUrl(from.fullPath);
    } else if (!isSSR) {
      captureOnlineMessagePreviousUrl(document.referrer);
    }
    return true;
  });

  // 全局守卫：检查 GSCountryCode Cookie 是否与当前页面匹配，不匹配则跳转
  router.beforeEach((to) => {
    const targetCountry = getCountryFromCookie();
    if (!targetCountry) return true;

    // 从路径中提取语言前缀，例如从 /en/news 提取 'en'，从 /ja-JP/news 提取 'ja-JP'
    const pathSegments = to.path.split('/');
    const currentLangPrefix = pathSegments[1] || ''; // 路径通常以 / 开头，所以 [0] 是空字符串

    // 判断当前语言前缀是否已经是目标国家
    // 简单匹配：如果前缀包含目标国家码（如 ja-JP 包含 JP），或者前缀等于目标国家码，则视为匹配
    if (
      currentLangPrefix.toUpperCase() === targetCountry ||
      currentLangPrefix.endsWith(`-${targetCountry}`)
    ) {
      return true;
    }

    // 构造新的跳转路径：将语言前缀替换为目标国家码
    // 假设路径结构为 /{lang}/{...rest}
    const restPathSegments = pathSegments.slice(2); // 移除空字符串和当前语言前缀
    const newPath =
      `/${targetCountry}/${restPathSegments.join('/')}`.replace(/\/+$/, '/') || `/${targetCountry}`;

    // 返回跳转的路由信息，添加 autoRedirect 标记以便在页面不存在时进行兜底处理
    return { path: newPath, query: { ...to.query, autoRedirect: '1' }, hash: to.hash };
  });

  // 每次路由变化后捕获营销来源并写入 LinkSource cookie
  router.afterEach((to) => {
    const q = to.query || {};
    captureLinkSource({
      src: q.src,
      utm_source: q.utm_source,
      utm_medium: q.utm_medium,
    }).catch(() => {});
  });

  return router;
}
